/**
 * Tour interactivo del Dashboard de Documentos de Vehículos
 *
 * Ruta activa: /alerts-vehicles  →  DashboardVehicleDocuments
 * Prefijo CSS: DVR__ (DashboardVehicleDocuments)
 *
 * Flujo automático:
 *  1. Contenedor principal — visión general
 *  2. Barra de controles: búsqueda + toggle de filtros
 *  3. Campo de búsqueda por placa
 *  4. Botón ☰ → ABRE el panel de filtros (onHighlightStarted)
 *  5. Panel de filtros — tipo de vehículo, operación y ordenamiento
 *  6. Layout principal → CIERRA el panel de filtros (onHighlightStarted)
 *  7. Panel lateral de vehículos
 *  8. Cabecera del panel con contador
 *  9. Lista de vehículos (tarjetas con estado)
 * 10. Paginación
 * 11. Panel de documentos — estado vacío (sin vehículo elegido)
 *
 * @param {string} theme     — tema activo ('light' | 'dark')
 * @param {object} driverRef — ref con la instancia de driver.js
 */
export const createVehicleDocumentsTour = (theme, driverRef) => {
  const t = theme;

  // ── Helpers DOM ────────────────────────────────────────────────────────

  const openMainFilters = () => {
    const isOpen = !!document.querySelector(`.DVR__${t}-dashboard-vd-filters-panel`);
    if (!isOpen) document.querySelector(`.DVR__${t}-dashboard-vd-filter-toggle`)?.click();
  };

  const closeMainFilters = () => {
    const isOpen = !!document.querySelector(`.DVR__${t}-dashboard-vd-filters-panel`);
    if (isOpen) document.querySelector(`.DVR__${t}-dashboard-vd-filter-toggle`)?.click();
  };

  return [
    // ── 1. Contenedor principal ──────────────────────────────────────────
    {
      element: `.DVR__${t}-dashboard-vd`,
      popover: {
        title: 'Dashboard de Documentos — Vehículos',
        description:
          'Vista centralizada del estado documental de toda la flota. ' +
          'A la izquierda se lista cada vehículo con un indicador de alerta; ' +
          'a la derecha se muestran los documentos del vehículo seleccionado. ' +
          'Los vehículos combinados (cabezote + trailer) permiten navegar entre ' +
          'ambos juegos de documentos sin abandonar la pantalla.',
        side: 'top',
        align: 'start',
      },
    },

    // ── 2. Área de controles ─────────────────────────────────────────────
    {
      element: `.DVR__${t}-dashboard-vd-controls`,
      popover: {
        title: 'Controles de búsqueda y filtrado',
        description:
          'Barra principal que concentra la búsqueda de vehículos por placa y el ' +
          'acceso al panel de filtros avanzados. Los filtros y la búsqueda se ' +
          'combinan para obtener resultados precisos.',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── 3. Campo de búsqueda ─────────────────────────────────────────────
    {
      element: `.DVR__${t}-dashboard-vd-search-wrap`,
      popover: {
        title: 'Buscar por placa',
        description:
          'Escribe la placa completa o parcial del vehículo. La búsqueda se envía ' +
          'al servidor con un retardo de 900 ms para evitar consultas innecesarias. ' +
          'El botón <strong>×</strong> limpia el campo y restablece todos los resultados.',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── 4. Toggle de filtros → ABRE panel (onHighlightStarted) ──────────
    {
      element: `.DVR__${t}-dashboard-vd-filter-toggle`,
      onHighlightStarted: openMainFilters,
      popover: {
        title: 'Filtros avanzados — abriendo',
        description:
          'El panel de filtros se ha abierto automáticamente. ' +
          'El botón <strong>☰</strong> actúa como interruptor: ' +
          'vuelve a pulsarlo para contraer el panel.',
        side: 'bottom',
        align: 'end',
      },
    },

    // ── 5. Panel de filtros ──────────────────────────────────────────────
    {
      element: `.DVR__${t}-dashboard-vd-filters-panel`,
      popover: {
        title: 'Filtros de la flota',
        description:
          'Tres selectores que se pueden combinar entre sí:<br><br>' +
          '• <strong>Tipo de vehículo</strong>: filtra por la categoría registrada en ' +
          'el sistema (camión, tracto, cisterna, etc.).<br>' +
          '• <strong>Operación</strong>: muestra solo los vehículos asignados a esa ' +
          'operación o cliente.<br>' +
          '• <strong>Ordenar por</strong>: organiza la lista por placa (A-Z / Z-A), ' +
          'estado (activos / inactivos) o alerta documental ' +
          '(vencidos, críticos &lt;10D, por vencer &lt;15D, próximos &lt;1M).',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── 6. Layout principal → CIERRA filtros (onHighlightStarted) ────────
    {
      element: `.DVR__${t}-dashboard-vd-layout`,
      onHighlightStarted: closeMainFilters,
      popover: {
        title: 'Diseño principal',
        description:
          'El dashboard se divide en dos paneles lado a lado: ' +
          'la <strong>lista de vehículos</strong> a la izquierda y los ' +
          '<strong>documentos del vehículo seleccionado</strong> a la derecha. ' +
          'En dispositivos pequeños, los paneles se apilan verticalmente.',
        side: 'top',
        align: 'start',
      },
    },

    // ── 7. Panel lateral de vehículos ────────────────────────────────────
    {
      element: `.DVR__${t}-dashboard-vd-vehicles-panel`,
      popover: {
        title: 'Panel de vehículos',
        description:
          'Lista paginada de vehículos que coinciden con los filtros activos. ' +
          'El contador en el encabezado indica el total encontrado y, si hay una ' +
          'búsqueda activa, la muestra entre comillas.',
        side: 'right',
        align: 'start',
      },
    },

    // ── 8. Cabecera del panel ────────────────────────────────────────────
    {
      element: `.DVR__${t}-dashboard-vd-panel-header`,
      popover: {
        title: 'Contador de vehículos',
        description:
          'Muestra cuántos vehículos coinciden con los filtros aplicados. ' +
          'Si hay una búsqueda de texto activa, aparece la leyenda ' +
          '"Filtrado por: &ldquo;…&rdquo;" junto al total.',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── 9. Lista de vehículos ────────────────────────────────────────────
    {
      element: `.DVR__${t}-dashboard-vd-vehicles-list`,
      popover: {
        title: 'Tarjetas de vehículo',
        description:
          'Cada tarjeta muestra:<br>' +
          '• <strong>Placa</strong> (o placa combinada cabezote + trailer).<br>' +
          '• <strong>Tipo</strong> y <strong>operación</strong> asignada.<br>' +
          '• Badge <em>Combinado</em> si el vehículo está enlazado a otro.<br>' +
          '• Estado <em>Activo / Inactivo</em> del vehículo.<br>' +
          '• Indicador de alerta documental: Vencido, Crítico, Por vencer, etc.<br><br>' +
          'Haz clic en una tarjeta para cargar sus documentos en el panel derecho.',
        side: 'right',
        align: 'start',
      },
    },

    // ── 10. Paginación ───────────────────────────────────────────────────
    {
      element: `.DVR__${t}-dashboard-vd-pagination`,
      popover: {
        title: 'Paginación',
        description:
          'Navega entre páginas con los botones <strong>‹ Anterior</strong> y ' +
          '<strong>Siguiente ›</strong>. El indicador central muestra la página actual ' +
          'y el número total. El tamaño de página se ajusta dinámicamente según la ' +
          'altura del contenedor para aprovechar el espacio disponible.',
        side: 'top',
        align: 'center',
      },
    },

    // ── 11. Panel de documentos (estado vacío) ───────────────────────────
    {
      element: `.DVR__${t}-dashboard-vd-docs-panel`,
      popover: {
        title: 'Panel de documentos',
        description:
          'Al seleccionar un vehículo, este panel muestra todos sus documentos.<br><br>' +
          'En el encabezado encontrarás:<br>' +
          '• <strong>Vista</strong>: alterna entre tarjetas y lista.<br>' +
          '• <strong>Cabezote / Trailer</strong>: si el vehículo es combinado, ' +
          'cambia entre los documentos de cada unidad sin perder el contexto.<br>' +
          '• <strong>☰ Filtros de documento</strong>: filtra por estado (vencido, ' +
          'crítico, próximo, faltante) y alterna entre documentos requeridos y extra.<br><br>' +
          'Cada tarjeta de documento indica su estado visual, el archivo adjunto con ' +
          'previsualización y el botón de edición para actualizarlo directamente.',
        side: 'left',
        align: 'start',
      },
    },
  ];
};
